import { DatePickerField } from "../conponents/DateRangePicker";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import styled from "styled-components";
import { GD } from "src/GD";
import { hasRole } from "src/permissions";
import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { AdminButton } from "../pages/administration/AdminControls";
import { reserveReleaseBounds, reserveReleaseRequest, validReserveReleaseDate } from "./releaseReserve";

const Dialog = styled.dialog`
	box-sizing: border-box; width: 460px; max-width: calc(100vw - 32px); max-height: 90dvh;
	padding: 0; border: 1px solid #d0d5dd; border-radius: 12px; background: #fff;
	font: 13px/1.5 "Euclid Circular B", sans-serif; color: #344054;
	&::backdrop { background: #10182880; }
	header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; padding: 20px; border-bottom: 1px solid #eaecf0; }
	.context { font-size: 11px; color: #667085; margin-bottom: 5px; }
	h2 { margin: 0; font-size: 21px; font-weight: 600; line-height: 1.3; color: #182230; }
	.close { display: flex; align-items: center; justify-content: center; padding: 0; width: 32px; height: 32px; border: 0; border-radius: 6px; background: transparent; color: #667085; cursor: pointer; }
	.body { padding: 20px; background: #f5f7fa; }
	.card { padding: 18px; border: 1px solid #d0d5dd; border-radius: 10px; background: #fff; box-shadow: 0 1px 2px rgba(16,24,40,.05); }
	input:focus-visible, button:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }
	.hint { margin: 10px 0 0; color: #667085; font-size: 12px; }
	.error { margin: 12px 0 0; color: #b42318; }
	footer { display: flex; justify-content: flex-end; gap: 8px; padding: 16px 20px; border-top: 1px solid #eaecf0; }
`;

export const ReleaseReservePopup = ({ sessionId, sessionIds, projectId, userId, onClose, onSaved }: {
	sessionId?: number; sessionIds?: readonly number[]; projectId: number; userId: number; onClose: () => void; onSaved: () => void;
}) => {
	useLocale();
	const ids = sessionIds ?? (sessionId !== undefined ? [sessionId] : []);
	const ref = useRef<HTMLDialogElement>(null);
	const request = useRef<AbortController | null>(null);
	const [date, setDate] = useState(() => reserveReleaseBounds().min);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const bounds = reserveReleaseBounds();
	useEffect(() => {
		const previous = document.activeElement;
		const dialog = ref.current;
		dialog?.showModal();
		const logout = GD.S_AUTH_LOG_OUT.subscribe(() => { request.current?.abort(); onClose(); });
		return () => {
			request.current?.abort();
			GD.S_AUTH_LOG_OUT.unsubscribe(logout);
			dialog?.close();
			if (previous instanceof HTMLElement && previous.isConnected) previous.focus({ preventScroll: true });
		};
	}, []);
	const submit = async () => {
		if (request.current || !ids.length) return;
		if (!validReserveReleaseDate(date)) { setError(msg("reserveReleaseInvalidDate")); return; }
		const controller = new AbortController();
		request.current = controller;
		setBusy(true); setError("");
		try {
			const [profile, project] = await Promise.all([GD.REQ_PROFILE.request(), GD.REQ_CURRENT_PROJECT.request()]);
			controller.signal.throwIfAborted();
			if (profile?.id !== userId || project?.id !== projectId || !hasRole(profile.projects.find(item => item.id === projectId)?.roles, "admin")) throw new Error(msg("reserveReleaseContextChanged"));
			// Revalidate UTC bounds at submission time, including after midnight.
			if (!validReserveReleaseDate(date)) throw new Error(msg("reserveReleaseInvalidDate"));
			const response = await GD.REQ_API.request({ ...reserveReleaseRequest(ids, date, projectId), signal: controller.signal });
			controller.signal.throwIfAborted();
			if (response.error) throw new Error(response.error.message || msg("reserveReleaseFailed"));
			onSaved();
		} catch (cause) {
			if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : msg("reserveReleaseFailed"));
		} finally {
			if (!controller.signal.aborted) { request.current = null; setBusy(false); }
		}
	};
	return createPortal(<Dialog ref={ref} aria-label={msg("reserveReleaseTitle")} aria-busy={busy} onKeyDown={event => event.stopPropagation()} onCancel={event => { event.preventDefault(); if (!busy) onClose(); }}>
		<header><div><div className="context">{ids.length === 1 ? msg("templatePayment_c88c6d69", { v0: ids[0] }) : msg("reserveSelectedCount", { count: ids.length })} · Rolling Reserve</div><h2>{msg("reserveReleaseTitle")}</h2></div><button className="close" type="button" disabled={busy} aria-label={msg("close")} onClick={onClose}><X size={18} /></button></header>
		<form onSubmit={event => { event.preventDefault(); void submit(); }}>
			<div className="body"><div className="card"><DatePickerField label={msg("reserveReleaseNewDate")} timeZone="UTC" size="medium" value={date} min={bounds.min} max={bounds.max} disabled={busy} portalContainer={() => ref.current ?? document.body} onChange={value => { setDate(value); setError(""); }} /><p className="hint" id="reserve-release-hint">{msg("reserveReleaseDateHint")}</p></div>{error && <p className="error" role="alert">{error}</p>}</div>
			<footer><AdminButton variant="secondarySmall" disabled={busy} onClick={onClose}>{msg("cancel")}</AdminButton><AdminButton variant="primarySmall" type="submit" disabled={busy || !ids.length || !validReserveReleaseDate(date)}>{msg(busy ? "reserveReleaseSaving" : "reserveReleaseConfirm")}</AdminButton></footer>
		</form>
	</Dialog>, document.body);
};
